import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { AddFacultyModal } from '../components/faculty/AddFacultyModal';
import { FacultyCommunicationsTab } from '../components/faculty/FacultyCommunicationsTab';
import { FacultyNotesTab } from '../components/faculty/FacultyNotesTab';
import { FacultyOverviewTab } from '../components/faculty/FacultyOverviewTab';
import { FacultyProfileHeader } from '../components/faculty/FacultyProfileHeader';
import { FacultyTeachingTab } from '../components/faculty/FacultyTeachingTab';
import type {
  FacultyCommunicationDraft,
  FacultyMember,
  FacultyNoteDraft,
  FacultyProfileDraft,
} from '../types/faculty';

type TabType = 'overview' | 'teaching' | 'notes' | 'communications';

interface FacultyProfilePageProps {
  faculty: FacultyMember;
  onBack: () => void;
  onUpdateFaculty: (id: string, updates: FacultyProfileDraft) => Promise<void> | void;
  onSaveNote?: (draft: FacultyNoteDraft) => Promise<void> | void;
  onComposeCommunication?: (draft: FacultyCommunicationDraft) => Promise<void> | void;
  initialTab?: TabType;
  successToast?: string;
}

const tabOptions: Array<{ id: TabType; label: string; testId: string }> = [
  { id: 'overview', label: 'Overview', testId: 'tab-overview' },
  { id: 'teaching', label: 'Assignments', testId: 'tab-teaching' },
  { id: 'notes', label: 'Notes', testId: 'tab-notes' },
  { id: 'communications', label: 'Communications', testId: 'tab-communications' },
];

export function FacultyProfilePage({
  faculty,
  onBack,
  onUpdateFaculty,
  onSaveNote,
  onComposeCommunication,
  initialTab = 'overview',
  successToast: _successToast,
}: FacultyProfilePageProps) {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCommunicationId, setSelectedCommunicationId] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [faculty.id, initialTab]);

  useEffect(() => {
    setSelectedCommunicationId(faculty.communications[0]?.id ?? null);
  }, [faculty.communications, faculty.id]);

  const handleSaveEdit = async (updates: FacultyProfileDraft) => {
    await onUpdateFaculty(faculty.id, updates);
    setIsEditModalOpen(false);
  };

  return (
    <section
      data-testid="faculty-live-profile"
      data-extracted-from="owned:ystnj/faculty/prd-profile"
      className="mx-auto max-w-[1440px] p-6 lg:p-0"
    >
      <button
        type="button"
        data-testid="back-to-faculty"
        onClick={onBack}
        className="mb-6 inline-flex items-center text-sm font-medium text-text-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to faculty
      </button>

      <FacultyProfileHeader
        faculty={faculty}
        onEdit={() => setIsEditModalOpen(true)}
        onContact={() => {
          setActiveTab('communications');
          setSelectedCommunicationId(faculty.communications[0]?.id ?? null);
        }}
      />

      <div className="mb-6 border-b border-border">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs" data-testid="profile-tabs">
          {tabOptions.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                data-testid={tab.testId}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:border-border-strong hover:text-iron-grey'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' ? <FacultyOverviewTab faculty={faculty} /> : null}
        {activeTab === 'teaching' ? <FacultyTeachingTab faculty={faculty} /> : null}
        {activeTab === 'notes' ? <FacultyNotesTab faculty={faculty} onSaveNote={onSaveNote} /> : null}
        {activeTab === 'communications' ? (
          <FacultyCommunicationsTab
            faculty={faculty}
            initialSelectedCommunicationId={selectedCommunicationId}
            onSelectedCommunicationChange={setSelectedCommunicationId}
            onComposeCommunication={onComposeCommunication}
          />
        ) : null}
      </div>

      <AddFacultyModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        initialData={faculty}
      />
    </section>
  );
}
